import {
  StartFullIndexingRequest,
  StartFullIndexingResponse,
  TargetedIndexingRequest,
  TargetedIndexingResponse,
  JobStatusResponse,
  ListJobsRequest,
  ListJobsResponse,
  CancelJobResponse,
} from '../types/bulkIndexing';
import { retryWithBackoff, isRetryableError } from '../utils/retry';
import { logger } from '../utils/logger';

// Get API base URL
// CRITICAL: Foundation API bulk-indexing endpoints are mounted at /api/v1/bulk-indexing/
// This function ensures we always use /api/v1 regardless of REACT_APP_API_BASE_URL setting
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_BASE_URL;
  
  // If REACT_APP_API_BASE_URL is set, check if it already includes /v1
  if (envUrl) {
    // If it already ends with /v1, use it as-is
    if (envUrl.endsWith('/v1') || envUrl.endsWith('/v1/')) {
      return envUrl.replace(/\/+$/, ''); // Remove trailing slashes
    }
    // If it's /api or ends with /api, append /v1
    if (envUrl === '/api' || envUrl.endsWith('/api')) {
      return `${envUrl.replace(/\/+$/, '')}/v1`;
    }
    // If it's a full URL without /api, append /api/v1
    // This handles cases like http://localhost:8003 -> http://localhost:8003/api/v1
    if (envUrl.startsWith('http://') || envUrl.startsWith('https://')) {
      // Check if it already has /api in the path
      if (envUrl.includes('/api')) {
        return `${envUrl.replace(/\/+$/, '')}/v1`;
      }
      return `${envUrl.replace(/\/+$/, '')}/api/v1`;
    }
    // For relative paths that don't match above, append /v1
    return `${envUrl.replace(/\/+$/, '')}/v1`;
  }
  
  // Default: Foundation API is mounted at /api/v1
  return '/api/v1';
};

const API_BASE_URL = getApiBaseUrl();
logger.info(`BulkIndexingService initialized with API_BASE_URL: ${API_BASE_URL}`);

/**
 * Bulk Indexing Service
 * 
 * Provides REST API client for bulk indexing operations in Foundation API.
 * Supports full database indexing and targeted indexing by account, workspace, or project.
 */
export const bulkIndexingService = {
  /**
   * Start full database indexing
   */
  async startFullIndexing(request: StartFullIndexingRequest = {}): Promise<StartFullIndexingResponse> {
    return retryWithBackoff(async () => {
      logger.info('Starting full database indexing', { request });
      
      const url = `${API_BASE_URL}/bulk-indexing/full/start`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(request),
      });
      
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: response.statusText || 'Unknown error', status: response.status };
        }
        
        const error = new Error(errorData.detail || errorData.message || 'Failed to start full indexing');
        (error as Error & { status?: number }).status = response.status;
        throw error;
      }
      
      const data = await response.json();
      logger.info('Successfully started full indexing', { jobId: data.job_id });
      return data;
    }, {
      isRetryable: (error) => {
        if (!isRetryableError(error)) {
          return false;
        }
        // Don't retry 4xx errors (client errors)
        const status = (error as Error & { status?: number }).status;
        if (status && status >= 400 && status < 500) {
          return false;
        }
        return true;
      },
    });
  },

  /**
   * Start account-targeted indexing
   */
  async startAccountIndexing(
    accountId: string,
    request: TargetedIndexingRequest = {}
  ): Promise<TargetedIndexingResponse> {
    return retryWithBackoff(async () => {
      logger.info('Starting account indexing', { accountId, request });
      
      const url = `${API_BASE_URL}/bulk-indexing/account/${accountId}/start`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(request),
      });
      
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: response.statusText || 'Unknown error', status: response.status };
        }
        
        const error = new Error(errorData.detail || errorData.message || 'Failed to start account indexing');
        (error as Error & { status?: number }).status = response.status;
        throw error;
      }
      
      const data = await response.json();
      logger.info('Successfully started account indexing', { jobId: data.job_id, accountId });
      return data;
    }, {
      isRetryable: (error) => {
        if (!isRetryableError(error)) {
          return false;
        }
        const status = (error as Error & { status?: number }).status;
        if (status && status >= 400 && status < 500) {
          return false;
        }
        return true;
      },
    });
  },

  /**
   * Start workspace-targeted indexing
   */
  async startWorkspaceIndexing(
    workspaceId: string,
    request: TargetedIndexingRequest = {}
  ): Promise<TargetedIndexingResponse> {
    return retryWithBackoff(async () => {
      logger.info('Starting workspace indexing', { workspaceId, request });
      
      const url = `${API_BASE_URL}/bulk-indexing/workspace/${workspaceId}/start`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(request),
      });
      
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: response.statusText || 'Unknown error', status: response.status };
        }
        
        const error = new Error(errorData.detail || errorData.message || 'Failed to start workspace indexing');
        (error as Error & { status?: number }).status = response.status;
        throw error;
      }
      
      const data = await response.json();
      logger.info('Successfully started workspace indexing', { jobId: data.job_id, workspaceId });
      return data;
    }, {
      isRetryable: (error) => {
        if (!isRetryableError(error)) {
          return false;
        }
        const status = (error as Error & { status?: number }).status;
        if (status && status >= 400 && status < 500) {
          return false;
        }
        return true;
      },
    });
  },

  /**
   * Start project-targeted indexing
   */
  async startProjectIndexing(
    projectId: string | number,
    request: TargetedIndexingRequest = {}
  ): Promise<TargetedIndexingResponse> {
    return retryWithBackoff(async () => {
      logger.info('Starting project indexing', { projectId, request });
      
      const url = `${API_BASE_URL}/bulk-indexing/project/${String(projectId)}/start`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(request),
      });
      
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: response.statusText || 'Unknown error', status: response.status };
        }
        
        const error = new Error(errorData.detail || errorData.message || 'Failed to start project indexing');
        (error as Error & { status?: number }).status = response.status;
        throw error;
      }
      
      const data = await response.json();
      logger.info('Successfully started project indexing', { jobId: data.job_id, projectId });
      return data;
    }, {
      isRetryable: (error) => {
        if (!isRetryableError(error)) {
          return false;
        }
        const status = (error as Error & { status?: number }).status;
        if (status && status >= 400 && status < 500) {
          return false;
        }
        return true;
      },
    });
  },

  /**
   * Get job status
   */
  async getJobStatus(jobId: string): Promise<JobStatusResponse> {
    return retryWithBackoff(async () => {
      logger.debug('Getting job status', { jobId });
      
      const url = `${API_BASE_URL}/bulk-indexing/jobs/${jobId}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: response.statusText || 'Unknown error', status: response.status };
        }
        
        const error = new Error(errorData.detail || errorData.message || 'Failed to get job status');
        (error as Error & { status?: number }).status = response.status;
        throw error;
      }
      
      const data = await response.json();
      return data;
    }, {
      maxAttempts: 2, // Fewer retries for status checks
      isRetryable: (error) => {
        if (!isRetryableError(error)) {
          return false;
        }
        const status = (error as Error & { status?: number }).status;
        // Don't retry 404 (job not found) or 4xx errors
        if (status === 404 || (status && status >= 400 && status < 500)) {
          return false;
        }
        return true;
      },
    });
  },

  /**
   * List jobs with optional filters
   */
  async listJobs(request: ListJobsRequest = {}): Promise<ListJobsResponse> {
    return retryWithBackoff(async () => {
      logger.debug('Listing jobs', { filters: request });
      
      const params = new URLSearchParams();
      if (request.status) params.append('status', request.status);
      if (request.target_type) params.append('target_type', request.target_type);
      if (request.limit !== undefined) params.append('limit', request.limit.toString());
      if (request.skip !== undefined) params.append('skip', request.skip.toString());
      
      const url = `${API_BASE_URL}/bulk-indexing/jobs?${params.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: response.statusText || 'Unknown error', status: response.status };
        }
        
        const error = new Error(errorData.detail || errorData.message || 'Failed to list jobs');
        (error as Error & { status?: number }).status = response.status;
        throw error;
      }
      
      const data = await response.json();
      return data;
    }, {
      maxAttempts: 2, // Fewer retries for list operations
      isRetryable: (error) => {
        if (!isRetryableError(error)) {
          return false;
        }
        const status = (error as Error & { status?: number }).status;
        if (status && status >= 400 && status < 500) {
          return false;
        }
        return true;
      },
    });
  },

  /**
   * Cancel a running job
   */
  async cancelJob(jobId: string): Promise<CancelJobResponse> {
    return retryWithBackoff(async () => {
      logger.info('Cancelling job', { jobId });
      
      const url = `${API_BASE_URL}/bulk-indexing/jobs/${jobId}/cancel`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: response.statusText || 'Unknown error', status: response.status };
        }
        
        const error = new Error(errorData.detail || errorData.message || 'Failed to cancel job');
        (error as Error & { status?: number }).status = response.status;
        throw error;
      }
      
      const data = await response.json();
      logger.info('Successfully cancelled job', { jobId });
      return data;
    }, {
      maxAttempts: 2, // Fewer retries for cancel operations
      isRetryable: (error) => {
        if (!isRetryableError(error)) {
          return false;
        }
        const status = (error as Error & { status?: number }).status;
        // Don't retry 404 (job not found) or 4xx errors
        if (status === 404 || (status && status >= 400 && status < 500)) {
          return false;
        }
        return true;
      },
    });
  },

  /**
   * Stop a running job (alias for cancelJob)
   */
  async stopJob(jobId: string): Promise<CancelJobResponse> {
    return this.cancelJob(jobId);
  },
};



